import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Icon,
  IconButton,
  Avatar,
  Badge,
  Button,
  ButtonGroup,
  Spinner,
  Center,
  Select as ChakraSelect,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiX,
  FiUserPlus,
  FiClock,
  FiMail,
  FiPhone,
  FiBriefcase,
  FiChevronRight,
  FiChevronDown,
  FiUsers,
  FiPlus,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { groupCRMUsersByDTC, sortCRMUsersWithDTCPriority } from '../../../utils/crmUserSorting';
import ContactTypeBadges from '../../../components/CRM/ContactTypeBadges';
import StageBadge from '../../../components/CRM/StageBadge';
import QuickAddContact from '../../../components/CRM/QuickAddContact';
import { useWorkspaceSearch, countOpenDeals, dealDisplayLabel, MIN_SEARCH_CHARS } from './useWorkspaceSearch';
import PeopleWorkbench from './PeopleWorkbench';
import DealsWorkbench from './DealsWorkbench';
import NewDealLauncher from './NewDealLauncher';

/**
 * ============================================================================
 * PREVIEW — Person Workspace search + workbench (/crm/workspace)
 * ============================================================================
 *
 * Two layers, one search box:
 *
 *  1. QUICK FIND (overlay) — type a name/email/phone/loan #/Arive ID, get
 *     person cards instantly, Enter opens the workspace. People with zero
 *     deals show up just as readily as people with three.
 *
 *  2. WORKBENCH (below) — the merged Dashboard + My Deals experience. Two
 *     lenses over the same search term and owner scope: People (contacts
 *     table) and Deals (deals table), each with its classic filter toolbar,
 *     smart lists, column customization, select-all and bulk actions —
 *     plus one-click preset chips and NEW bulk campaign enrollment.
 *     Every row lands in /crm/workspace/:contactId.
 *
 * Allowlist-gated by WorkspacePreviewRoute — only visible to preview users.
 * ============================================================================
 */

const OWNER_KEY = 'workspaceWorkbenchOwner';
const LENS_KEY = 'workspaceWorkbenchLens';
const BENCH_TERM_DEBOUNCE_MS = 450;

const formatAmount = (amount) => {
  if (!amount || amount <= 0) return null;
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${Math.round(amount / 1000)}K`;
  return `$${amount}`;
};

function PersonCard({ person, isHighlighted, showOwner, ownerName, onOpen, innerRef }) {
  const cardBG = useColorModeValue('white', 'gray.800');
  const highlightBG = useColorModeValue('purple.50', 'purple.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const highlightBorder = useColorModeValue('purple.400', 'purple.400');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const { contact, deals } = person;
  const openCount = countOpenDeals(deals);
  const shownDeals = deals.slice(0, 3);

  return (
    <Box
      ref={innerRef}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen();
      }}
      bg={isHighlighted ? highlightBG : cardBG}
      borderWidth="1px"
      borderColor={isHighlighted ? highlightBorder : borderColor}
      borderRadius="lg"
      px={4}
      py={3}
      cursor="pointer"
      transition="border-color 0.15s, background 0.15s"
      _hover={{ borderColor: highlightBorder }}
    >
      <HStack spacing={3} align="flex-start">
        <Avatar size="md" name={`${contact.firstName || ''} ${contact.lastName || ''}`} />
        <VStack align="stretch" spacing={1} flex={1} minW={0}>
          <HStack spacing={2} flexWrap="wrap">
            <Text fontWeight="bold" noOfLines={1}>
              {contact.firstName} {contact.lastName}
            </Text>
            <ContactTypeBadges
              contactTypes={contact.contactTypes}
              primaryContactType={contact.primaryContactType}
              maxDisplay={2}
            />
            {(contact.dnc || contact.dncCall || contact.dncEmail || contact.dncSms) && (
              <Badge colorScheme="red" fontSize="2xs">DNC</Badge>
            )}
            {showOwner && (
              <Badge colorScheme="purple" fontSize="2xs" maxW="160px" isTruncated>
                {ownerName || 'Unassigned'}
              </Badge>
            )}
          </HStack>
          <HStack spacing={4} color={mutedColor} fontSize="sm" flexWrap="wrap">
            {contact.email && (
              <HStack spacing={1} minW={0}>
                <Icon as={FiMail} boxSize={3} flexShrink={0} />
                <Text noOfLines={1}>{contact.email}</Text>
              </HStack>
            )}
            {contact.phone && (
              <HStack spacing={1}>
                <Icon as={FiPhone} boxSize={3} />
                <Text>{contact.phone}</Text>
              </HStack>
            )}
            {contact.company && (
              <HStack spacing={1} minW={0}>
                <Icon as={FiBriefcase} boxSize={3} flexShrink={0} />
                <Text noOfLines={1}>{contact.company}</Text>
              </HStack>
            )}
          </HStack>
          <HStack spacing={2} flexWrap="wrap" pt={1}>
            {deals.length === 0 ? (
              <Badge colorScheme="gray" variant="subtle" fontSize="2xs">
                No deals yet
              </Badge>
            ) : (
              <>
                {shownDeals.map((deal) => (
                  <HStack
                    key={deal.id}
                    spacing={1.5}
                    px={1.5}
                    py={0.5}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    minW={0}
                  >
                    <StageBadge stage={deal.stage} />
                    <Text fontSize="2xs" fontWeight="semibold" noOfLines={1} maxW="260px">
                      {dealDisplayLabel(deal)}
                    </Text>
                    {formatAmount(deal.amount) && (
                      <Text fontSize="2xs" fontWeight="semibold" color="green.500" flexShrink={0}>
                        {formatAmount(deal.amount)}
                      </Text>
                    )}
                  </HStack>
                ))}
                {deals.length > shownDeals.length && (
                  <Badge colorScheme="gray" fontSize="2xs">+{deals.length - shownDeals.length} more</Badge>
                )}
                {openCount >= 2 && (
                  <Badge colorScheme="orange" fontSize="2xs">{openCount} active</Badge>
                )}
              </>
            )}
          </HStack>
        </VStack>
        <Icon as={FiChevronRight} color={mutedColor} alignSelf="center" />
      </HStack>
    </Box>
  );
}

export default function WorkspaceSearchPage() {
  const history = useHistory();
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  /* ---------------- Search term (live for overlay, debounced for tables) --- */
  const [term, setTerm] = useState('');
  const [benchTerm, setBenchTerm] = useState('');
  useEffect(() => {
    const trimmed = term.trim();
    const next = trimmed.length >= MIN_SEARCH_CHARS ? trimmed : '';
    const timer = setTimeout(() => setBenchTerm(next), BENCH_TERM_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  /* ---------------- Lens + owner scope (persisted) ---------------- */
  const [lens, setLens] = useState(() => {
    try {
      return localStorage.getItem(LENS_KEY) === 'deals' ? 'deals' : 'people';
    } catch (_) {
      return 'people';
    }
  });
  const changeLens = (next) => {
    setLens(next);
    try {
      localStorage.setItem(LENS_KEY, next);
    } catch (_) {}
  };

  // '' = All Users (admins only); a uid = that user's book
  const [selectedUserId, setSelectedUserId] = useState(() => {
    try {
      return localStorage.getItem(OWNER_KEY) || '';
    } catch (_) {
      return '';
    }
  });
  const changeOwner = (uid) => {
    setSelectedUserId(uid);
    try {
      localStorage.setItem(OWNER_KEY, uid);
    } catch (_) {}
  };

  /* ---------------- CRM users (owner select, badges, bulk modals) ---------- */
  const [crmUsers, setCrmUsers] = useState([]);
  useEffect(() => {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('CRMUser', '==', true));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const users = snapshot.docs.map((d) => ({
        uid: d.id,
        name: d.data().Name || d.data().name || d.data().email,
        ...d.data(),
      }));
      setCrmUsers(sortCRMUsersWithDTCPriority(users));
    });
    return () => unsubscribe();
  }, []);
  const usersMap = useMemo(() => {
    const map = new Map();
    for (const u of crmUsers) map.set(u.uid, u.name);
    return map;
  }, [crmUsers]);

  // Dialpad gates the Bulk SMS action (same check as the dashboard)
  const [dialpadEnabled, setDialpadEnabled] = useState(false);
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsubscribe = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setDialpadEnabled(data.dialpadCapable === true && data.dialpadEnabled === true);
      }
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  /* ---------------- Quick-find overlay ---------------- */
  // Hero search shares the workbench owner scope: a picked user narrows the
  // overlay to their book; All Users searches everyone.
  const heroScope = isCRMAdmin ? (selectedUserId ? 'mine' : 'all') : 'mine';
  const heroUid = isCRMAdmin ? selectedUserId : currentUser?.uid;
  const { people, loading, isSearchMode } = useWorkspaceSearch(term, heroScope, heroUid);

  const [overlayOpen, setOverlayOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef(null);
  const heroRef = useRef(null);
  const highlightedCardRef = useRef(null);

  useOutsideClick({ ref: heroRef, handler: () => setOverlayOpen(false) });

  useEffect(() => {
    setHighlightIndex(0);
  }, [people]);

  useEffect(() => {
    highlightedCardRef.current?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  const { isOpen: isAddContactOpen, onOpen: onAddContactOpen, onClose: onAddContactClose } = useDisclosure();
  const { isOpen: isNewDealOpen, onOpen: onNewDealOpen, onClose: onNewDealClose } = useDisclosure();

  // Live totals from the workbench tables — shown next to the lens toggle
  const [peopleCount, setPeopleCount] = useState(0);
  const [dealsCount, setDealsCount] = useState(0);

  const openPerson = (contactId, dealId) => {
    if (!contactId) return;
    history.push(`/crm/workspace/${contactId}${dealId ? `?deal=${dealId}` : ''}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOverlayOpen(true);
      setHighlightIndex((i) => Math.min(i + 1, Math.max(people.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const person = people[highlightIndex];
      if (person && overlayOpen) openPerson(person.contact.id);
    } else if (e.key === 'Escape') {
      if (term) {
        setTerm('');
      } else {
        setOverlayOpen(false);
        inputRef.current?.blur();
      }
    }
  };

  // Prefill "Create contact" from the search words (best-effort name split)
  const prefillFromTerm = useMemo(() => {
    const words = term.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return {};
    if (term.includes('@')) return { email: term.trim() };
    if (/^[\d\s()+-]+$/.test(term.trim())) return { phone: term.trim() };
    return { firstName: words[0] || '', lastName: words.slice(1).join(' ') || '' };
  }, [term]);

  /* ---------------- Style values ---------------- */
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const bannerBG = useColorModeValue('purple.600', 'purple.500');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const inputBG = useColorModeValue('white', 'gray.800');
  const overlayBG = useColorModeValue('white', 'gray.800');
  const overlayBorder = useColorModeValue('gray.200', 'gray.600');
  const barBG = useColorModeValue('gray.100', 'gray.700');
  const barBorder = useColorModeValue('gray.200', 'gray.600');

  const showOwnerBadges = isCRMAdmin && !selectedUserId;
  const overlayVisible = overlayOpen && (isSearchMode || people.length > 0 || loading);

  return (
    <Box bg={bgColor} minH="100vh">
      {/* PREVIEW banner (POC pattern) */}
      <Box bg={bannerBG} color="white" py={2} px={4} textAlign="center">
        <Text fontSize="sm" fontWeight="bold">
          PERSON WORKSPACE — PREVIEW · Only visible to you · Live data, real saves
        </Text>
      </Box>

      <Container maxW="1600px" px={{ base: 4, md: 6, lg: 8 }} py={{ base: 3, md: 4 }}>
        <VStack spacing={3} align="stretch">
          {/* ------------------- Control bar: lens · search · scope · new -------
              One gray row = the whole searchable/filter area. The quick-find
              overlay anchors to the search input, so results float over the
              workbench without pushing it around. ------------------- */}
          <Box
            bg={barBG}
            borderWidth="1px"
            borderColor={barBorder}
            borderRadius="xl"
            px={{ base: 2, md: 3 }}
            py={2}
          >
            <HStack spacing={2} flexWrap="wrap" align="center">
              {/* Lens toggle + live count (replaces the old table header) */}
              <HStack spacing={2} flexShrink={0}>
                <ButtonGroup size="sm" isAttached variant="outline">
                  <Button
                    leftIcon={<FiUsers />}
                    colorScheme={lens === 'people' ? 'purple' : 'gray'}
                    variant={lens === 'people' ? 'solid' : 'outline'}
                    bg={lens === 'people' ? undefined : inputBG}
                    onClick={() => changeLens('people')}
                  >
                    People
                  </Button>
                  <Button
                    leftIcon={<FiBriefcase />}
                    colorScheme={lens === 'deals' ? 'blue' : 'gray'}
                    variant={lens === 'deals' ? 'solid' : 'outline'}
                    bg={lens === 'deals' ? undefined : inputBG}
                    onClick={() => changeLens('deals')}
                  >
                    Deals
                  </Button>
                </ButtonGroup>
                <Badge
                  colorScheme={lens === 'people' ? 'purple' : 'blue'}
                  fontSize="sm"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                >
                  {(lens === 'people' ? peopleCount : dealsCount).toLocaleString()}
                </Badge>
              </HStack>

              {/* Search — quick-find overlay anchor */}
              <Box
                position="relative"
                ref={heroRef}
                flex="1 1 260px"
                minW={{ base: '100%', md: '260px' }}
                order={{ base: 3, md: 0 }}
              >
                <InputGroup size="md">
                  <InputLeftElement pointerEvents="none">
                    {loading ? <Spinner size="sm" color="purple.400" /> : <Icon as={FiSearch} color="gray.400" />}
                  </InputLeftElement>
                  <Input
                    ref={inputRef}
                    autoFocus
                    bg={inputBG}
                    placeholder="Find a person — name, email, phone, company, loan #, or Arive ID"
                    value={term}
                    onChange={(e) => {
                      setTerm(e.target.value);
                      setOverlayOpen(true);
                    }}
                    onFocus={() => setOverlayOpen(true)}
                    onKeyDown={handleKeyDown}
                    borderRadius="lg"
                  />
                  {term && (
                    <InputRightElement>
                      <IconButton
                        aria-label="Clear search"
                        icon={<FiX />}
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setTerm('');
                          inputRef.current?.focus();
                        }}
                      />
                    </InputRightElement>
                  )}
                </InputGroup>

                {/* Quick-find overlay — results float over the workbench, so the
                    tables below never jump while typing */}
                {overlayVisible && (
                  <Box
                    position="absolute"
                    top="calc(100% + 8px)"
                    left={0}
                    right={0}
                    zIndex={30}
                    bg={overlayBG}
                    borderWidth="1px"
                    borderColor={overlayBorder}
                    borderRadius="xl"
                    shadow="2xl"
                    maxH="440px"
                    overflowY="auto"
                    p={2}
                  >
                    <VStack spacing={2} align="stretch">
                      {!isSearchMode && people.length > 0 && (
                        <HStack spacing={2} color={mutedColor} px={2} pt={1}>
                          <Icon as={FiClock} boxSize={3.5} />
                          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase">
                            Recently opened
                          </Text>
                        </HStack>
                      )}

                      {people.map((person, idx) => (
                        <PersonCard
                          key={person.contact.id}
                          person={person}
                          isHighlighted={idx === highlightIndex}
                          innerRef={idx === highlightIndex ? highlightedCardRef : undefined}
                          showOwner={showOwnerBadges}
                          ownerName={usersMap.get(person.contact.ownerId)}
                          onOpen={() => openPerson(person.contact.id)}
                        />
                      ))}

                      {isSearchMode && !loading && people.length === 0 && (
                        <Center py={6}>
                          <VStack spacing={3}>
                            <Icon as={FiSearch} boxSize={8} color="gray.300" />
                            <Text color={mutedColor} fontSize="sm">No people found for “{term.trim()}”</Text>
                            <Button size="sm" leftIcon={<FiUserPlus />} colorScheme="purple" onClick={onAddContactOpen}>
                              Create “{term.trim()}” as a new contact
                            </Button>
                          </VStack>
                        </Center>
                      )}
                    </VStack>
                  </Box>
                )}
              </Box>

              {/* Owner scope (admins) + create */}
              <HStack spacing={2} flexShrink={0} ml={{ md: 'auto' }}>
                {isCRMAdmin && (
                  <ChakraSelect
                    size="sm"
                    maxW="200px"
                    bg={inputBG}
                    borderRadius="md"
                    placeholder="All Users"
                    value={selectedUserId}
                    onChange={(e) => changeOwner(e.target.value)}
                  >
                    {(() => {
                      const { dtcUsers, otherUsers } = groupCRMUsersByDTC(crmUsers);
                      return (
                        <>
                          {dtcUsers.length > 0 && (
                            <optgroup label="DTC Loan Officers">
                              {dtcUsers.map((user) => (
                                <option key={user.uid} value={user.uid}>{user.name}</option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label={dtcUsers.length > 0 ? 'Other Users' : 'Users'}>
                            {otherUsers.map((user) => (
                              <option key={user.uid} value={user.uid}>{user.name}</option>
                            ))}
                          </optgroup>
                        </>
                      );
                    })()}
                  </ChakraSelect>
                )}
                <Menu autoSelect={false}>
                  <MenuButton
                    as={Button}
                    size="sm"
                    colorScheme="purple"
                    leftIcon={<FiPlus />}
                    rightIcon={<Icon as={FiChevronDown} boxSize={3} />}
                  >
                    New
                  </MenuButton>
                  <MenuList zIndex={40}>
                    <MenuItem icon={<FiUserPlus />} onClick={onAddContactOpen}>
                      New Contact
                    </MenuItem>
                    <MenuItem icon={<FiBriefcase />} onClick={onNewDealOpen}>
                      New Deal
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </HStack>
          </Box>

          {/* ------------------- Workbench lenses -------------------
              Both stay mounted so filters/selection survive lens switches;
              only the active one is displayed. */}
          <Box display={lens === 'people' ? 'block' : 'none'}>
            <PeopleWorkbench
              currentUser={currentUser}
              isCRMAdmin={isCRMAdmin}
              selectedUserId={selectedUserId}
              crmUsers={crmUsers}
              usersMap={usersMap}
              dialpadEnabled={dialpadEnabled}
              searchTerm={benchTerm}
              onOpenPerson={openPerson}
              onCountChange={setPeopleCount}
            />
          </Box>
          <Box display={lens === 'deals' ? 'block' : 'none'}>
            <DealsWorkbench
              currentUser={currentUser}
              isCRMAdmin={isCRMAdmin}
              selectedUserId={selectedUserId}
              crmUsers={crmUsers}
              usersMap={usersMap}
              searchTerm={benchTerm}
              onOpenPerson={openPerson}
              onCountChange={setDealsCount}
            />
          </Box>
        </VStack>
      </Container>

      {/* Dead-end escape hatch: create the person right here, land in their
          workspace. Mounted on open so prefilledData (useState initializers
          inside QuickAddContact) reflects the CURRENT search term. */}
      {isAddContactOpen && (
        <QuickAddContact
          isOpen={isAddContactOpen}
          onClose={onAddContactClose}
          prefilledData={prefillFromTerm}
          onContactCreated={(newContact) => {
            if (newContact?.id) openPerson(newContact.id);
          }}
        />
      )}

      {/* New Deal — pick the person first (QuickAddDeal needs a contact),
          then the standard quick-deal form; lands in the workspace. */}
      {isNewDealOpen && (
        <NewDealLauncher
          isOpen={isNewDealOpen}
          onClose={onNewDealClose}
          isCRMAdmin={isCRMAdmin}
          selectedUserId={selectedUserId}
          onDealCreated={(deal) => {
            if (deal?.contactId) openPerson(deal.contactId, deal.id);
          }}
        />
      )}
    </Box>
  );
}
